export interface DashboardMeta {
  id: string;
  name: string;
  status: 'draft' | 'published';
  isDefault?: boolean;
}

export interface DashboardManagerState {
  dashboards: DashboardMeta[];
  activeDashboardId: string | null;
  isLoading: boolean;
  isCreating: boolean;
  isDeleting: boolean;
  isRenaming: boolean;
  isPublishing: boolean;
  error: string | null;
}

export const initialDashboardManagerState: DashboardManagerState = {
  dashboards: [],
  activeDashboardId: null,
  isLoading: true,
  isCreating: false,
  isDeleting: false,
  isRenaming: false,
  isPublishing: false,
  error: null,
};

export enum DashboardManagerActionTypes {
  SET_LOADING = 'DASHBOARD_MANAGER/SET_LOADING',
  SET_CREATING = 'DASHBOARD_MANAGER/SET_CREATING',
  SET_DELETING = 'DASHBOARD_MANAGER/SET_DELETING',
  SET_RENAMING = 'DASHBOARD_MANAGER/SET_RENAMING',
  SET_PUBLISHING = 'DASHBOARD_MANAGER/SET_PUBLISHING',
  SET_ERROR = 'DASHBOARD_MANAGER/SET_ERROR',
  SET_DASHBOARDS = 'DASHBOARD_MANAGER/SET_DASHBOARDS',
  SET_ACTIVE_DASHBOARD = 'DASHBOARD_MANAGER/SET_ACTIVE_DASHBOARD',
  ADD_DASHBOARD = 'DASHBOARD_MANAGER/ADD_DASHBOARD',
  UPDATE_DASHBOARD = 'DASHBOARD_MANAGER/UPDATE_DASHBOARD',
  REMOVE_DASHBOARD = 'DASHBOARD_MANAGER/REMOVE_DASHBOARD',
  RESET_FOR_COMPANY_CONTEXT = 'DASHBOARD_MANAGER/RESET_FOR_COMPANY_CONTEXT',
}

export type DashboardManagerAction =
  | { type: DashboardManagerActionTypes.RESET_FOR_COMPANY_CONTEXT }
  | { type: DashboardManagerActionTypes.SET_LOADING; payload: boolean }
  | { type: DashboardManagerActionTypes.SET_CREATING; payload: boolean }
  | { type: DashboardManagerActionTypes.SET_DELETING; payload: boolean }
  | { type: DashboardManagerActionTypes.SET_RENAMING; payload: boolean }
  | { type: DashboardManagerActionTypes.SET_PUBLISHING; payload: boolean }
  | { type: DashboardManagerActionTypes.SET_ERROR; payload: string | null }
  | {
      type: DashboardManagerActionTypes.SET_DASHBOARDS;
      payload: { dashboards: DashboardMeta[]; activeDashboardId?: string };
    }
  | { type: DashboardManagerActionTypes.SET_ACTIVE_DASHBOARD; payload: string }
  | { type: DashboardManagerActionTypes.ADD_DASHBOARD; payload: DashboardMeta }
  | {
      type: DashboardManagerActionTypes.UPDATE_DASHBOARD;
      payload: DashboardMeta;
    }
  | { type: DashboardManagerActionTypes.REMOVE_DASHBOARD; payload: string };

export function dashboardManagerReducer(
  state: DashboardManagerState,
  action: DashboardManagerAction,
): DashboardManagerState {
  switch (action.type) {
    case DashboardManagerActionTypes.RESET_FOR_COMPANY_CONTEXT:
      return {
        ...initialDashboardManagerState,
        isLoading: true,
      };

    case DashboardManagerActionTypes.SET_LOADING:
      return {
        ...state,
        isLoading: action.payload,
      };

    case DashboardManagerActionTypes.SET_CREATING:
      return {
        ...state,
        isCreating: action.payload,
      };

    case DashboardManagerActionTypes.SET_DELETING:
      return {
        ...state,
        isDeleting: action.payload,
      };

    case DashboardManagerActionTypes.SET_RENAMING:
      return {
        ...state,
        isRenaming: action.payload,
      };

    case DashboardManagerActionTypes.SET_PUBLISHING:
      return {
        ...state,
        isPublishing: action.payload,
      };

    case DashboardManagerActionTypes.SET_ERROR:
      return {
        ...state,
        error: action.payload,
        isLoading: false,
        isCreating: false,
        isDeleting: false,
        isRenaming: false,
        isPublishing: false,
      };

    case DashboardManagerActionTypes.SET_DASHBOARDS: {
      const { dashboards, activeDashboardId } = action.payload;

      let activeId = activeDashboardId;

      if (!activeId) {
        const defaultDashboard = dashboards.find(d => d.isDefault === true);
        activeId = defaultDashboard?.id || dashboards[0]?.id || undefined;
      }

      return {
        ...state,
        dashboards,
        activeDashboardId: activeId || null,
        isLoading: false,
        error: null,
      };
    }

    case DashboardManagerActionTypes.SET_ACTIVE_DASHBOARD: {
      const dashboard = state.dashboards.find(d => d.id === action.payload);
      if (!dashboard) return state;

      return {
        ...state,
        activeDashboardId: action.payload,
      };
    }

    case DashboardManagerActionTypes.ADD_DASHBOARD: {
      return {
        ...state,
        dashboards: [...state.dashboards, action.payload],
        activeDashboardId: action.payload.id,
        isCreating: false,
      };
    }

    case DashboardManagerActionTypes.UPDATE_DASHBOARD: {
      const updatedDashboards = state.dashboards.map(d =>
        d.id === action.payload.id ? action.payload : d,
      );

      return {
        ...state,
        dashboards: updatedDashboards,
        isRenaming: false,
        isPublishing: false,
      };
    }

    case DashboardManagerActionTypes.REMOVE_DASHBOARD: {
      if (state.dashboards.length <= 1) return state;

      const updatedDashboards = state.dashboards.filter(
        d => d.id !== action.payload,
      );
      const needsNewActive = state.activeDashboardId === action.payload;
      const newActiveId = needsNewActive
        ? updatedDashboards[0]?.id
        : state.activeDashboardId;

      return {
        ...state,
        dashboards: updatedDashboards,
        activeDashboardId: newActiveId,
        isDeleting: false,
      };
    }

    default:
      return state;
  }
}

export const dashboardManagerActions = {
  setLoading: (isLoading: boolean): DashboardManagerAction => ({
    type: DashboardManagerActionTypes.SET_LOADING,
    payload: isLoading,
  }),

  setCreating: (isCreating: boolean): DashboardManagerAction => ({
    type: DashboardManagerActionTypes.SET_CREATING,
    payload: isCreating,
  }),

  setDeleting: (isDeleting: boolean): DashboardManagerAction => ({
    type: DashboardManagerActionTypes.SET_DELETING,
    payload: isDeleting,
  }),

  setRenaming: (isRenaming: boolean): DashboardManagerAction => ({
    type: DashboardManagerActionTypes.SET_RENAMING,
    payload: isRenaming,
  }),

  setPublishing: (isPublishing: boolean): DashboardManagerAction => ({
    type: DashboardManagerActionTypes.SET_PUBLISHING,
    payload: isPublishing,
  }),

  setError: (error: string | null): DashboardManagerAction => ({
    type: DashboardManagerActionTypes.SET_ERROR,
    payload: error,
  }),

  setDashboards: (
    dashboards: DashboardMeta[],
    activeDashboardId?: string,
  ): DashboardManagerAction => ({
    type: DashboardManagerActionTypes.SET_DASHBOARDS,
    payload: { dashboards, activeDashboardId },
  }),

  setActiveDashboard: (dashboardId: string): DashboardManagerAction => ({
    type: DashboardManagerActionTypes.SET_ACTIVE_DASHBOARD,
    payload: dashboardId,
  }),

  addDashboard: (dashboard: DashboardMeta): DashboardManagerAction => ({
    type: DashboardManagerActionTypes.ADD_DASHBOARD,
    payload: dashboard,
  }),

  updateDashboard: (dashboard: DashboardMeta): DashboardManagerAction => ({
    type: DashboardManagerActionTypes.UPDATE_DASHBOARD,
    payload: dashboard,
  }),

  removeDashboard: (dashboardId: string): DashboardManagerAction => ({
    type: DashboardManagerActionTypes.REMOVE_DASHBOARD,
    payload: dashboardId,
  }),

  resetForCompanyContext: (): DashboardManagerAction => ({
    type: DashboardManagerActionTypes.RESET_FOR_COMPANY_CONTEXT,
  }),
};
